import { findAgentItemBySelector, type ResolvableAgentItem } from '@/utils/agentRefs'

// Shared workflow-helper-token logic, used by both the Advanced WorkflowEditor and the simple-mode
// Automations lens. A helper token is `<agentSelector|#|dataKey>`, where the middle segment is the
// (configurable) delimiter. Keeping this in one place means the two surfaces parse and rewrite tokens
// identically.

const DEFAULT_DELIMITER = '|#|'

export function effectiveWorkflowDelimiter(delimiter?: string | null): string {
  return delimiter?.trim() || DEFAULT_DELIMITER
}

export function buildWorkflowHelperRegex(delimiter?: string | null): RegExp {
  const d = effectiveWorkflowDelimiter(delimiter)
  const escaped = d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`<((?:(?!${escaped}|>).)+)${escaped}([^>]+)>`, 'g')
}

// Rewrite helper tokens so the agent selector reads as its human name instead of a canonical ref
// (`agent/<id>`). This is the "normalizing agent" logic the Advanced editor uses for editing.
// - `ownedOnly` (Advanced editing): only owned refs are denormalized, because owned names are
//   re-normalized back to refs on save; shared selectors are left as canonical refs.
// - default (simple-mode display): owned and shared selectors both resolve to their name.
// Selectors that don't resolve are always left untouched.
export function denormalizeHelperAgentRefs(
  prompt: string,
  delimiter: string | null | undefined,
  agents: ResolvableAgentItem[],
  opts: { ownedOnly?: boolean } = {}
): string {
  if (!prompt) return prompt
  const d = effectiveWorkflowDelimiter(delimiter)
  return prompt.replace(buildWorkflowHelperRegex(delimiter), (fullMatch, selector: string, dataKey: string) => {
    const item = findAgentItemBySelector(selector.trim(), agents)
    if (!item || (opts.ownedOnly && item.kind !== 'owned')) return fullMatch
    return `<${item.title}${d}${dataKey.trim()}>`
  })
}

/** A helper-token edit: tokens matching `from` are rewritten to `to`, or removed when `to` is null. */
export interface HelperTokenEdit {
  from: { agent: string; input: string }
  to: { agent: string; input: string } | null
}

// The displayed form of a token's dataKey: a single `{{path}}` interpolation unwraps to `path`,
// anything else is shown as-is. Shared with the simple-mode lens (parseWorkflowHelpers) so parsing
// and rewriting always agree on what a chip's "input" is.
export function displayWorkflowDataKey(dataKey: string): string {
  const match = dataKey.trim().match(/^\{\{\s*([^{}]+?)\s*\}\}$/)
  return match ? match[1].trim() : dataKey.trim()
}

// Apply simple-mode helper-chip edits to a (denormalized) main prompt. A token matches an edit when
// its agent — resolved to a display title like the lens shows — and its displayed dataKey equal the
// edit's `from` pair; every matching occurrence is rewritten (or removed for a deletion). New values
// are written as plain names/keys: the same denormalized form the Advanced editor saves, which the
// backend normalizes back to canonical refs.
export function rewriteWorkflowHelperTokens(
  prompt: string,
  delimiter: string | null | undefined,
  edits: HelperTokenEdit[],
  agents: ResolvableAgentItem[]
): string {
  if (!prompt || !edits.length) return prompt
  const d = effectiveWorkflowDelimiter(delimiter)
  return prompt.replace(buildWorkflowHelperRegex(delimiter), (fullMatch, selector: string, dataKey: string) => {
    const item = findAgentItemBySelector(selector.trim(), agents)
    const title = item?.title ?? selector.trim()
    const input = displayWorkflowDataKey(dataKey)
    const edit = edits.find((e) => e.from.agent === title && e.from.input === input)
    if (!edit) return fullMatch
    if (!edit.to) return ''
    // Owned agents are written as names (the backend re-normalizes them to refs, as with Advanced
    // saves); a shared agent must be written as its canonical ref — a bare name can't be normalized.
    const toItem = findAgentItemBySelector(edit.to.agent, agents)
    const selectorOut = toItem && toItem.kind !== 'owned' ? toItem.ref : edit.to.agent
    // An unchanged input keeps the token's original dataKey text — rewriting it with the displayed
    // form would silently strip a `{{path}}` interpolation wrapper the user never touched.
    const dataKeyOut = edit.to.input === input ? dataKey.trim() : edit.to.input
    return `<${selectorOut}${d}${dataKeyOut}>`
  })
}
